/**
 * Pure derivations for the Now & Next rail (DNKADM-0298): the coach-load chart
 * and the "Room for a walk-in" panel that replace the retrospective bay-
 * occupancy strip (which measured the whole day and never referenced `now`).
 * Split out of `NowNextView.tsx` like `nowNextBuckets.ts` so the rules are
 * testable against a FIXED `now` without rendering — no function here ever
 * reads the real clock.
 *
 * Data model notes (from the approved ReservationsBoard design + the spec):
 *  - A **coach-required booking** is a reservation with a non-null
 *    `assignedStaffId` — never inferred from the service name. Bay Reservation
 *    resolves staff NONE and so carries none; lessons/evaluations/fittings do.
 *  - **Done** = the booking's end time has passed at `now`. **Still to come**
 *    = everything else, INCLUDING in-progress (a session happening right now
 *    has not finished, so it is still ahead of the coach). A booking with a
 *    missing/unparseable end cannot have "ended" → still to come.
 *  - **Cancelled is excluded everywhere**, consistent with the board
 *    (`nowNextBuckets`). Pending counts as booked — a pending request holds
 *    its slot, so the walk-in panel must not offer it away (design footnote).
 */
import type { Reservation } from '@/api/reservations';
import {
  compareStaffLabels,
  deriveStatusBucket,
  nowHourFraction,
  type HourRange,
} from './derive';

/** One roster row (id + display label) — the shell's staff/bay roster shape. */
export interface RosterEntry {
  id: string;
  label: string;
}

// ---- Coach load ---------------------------------------------------------------

export interface CoachLoadRow {
  id: string;
  /** Full roster label (orders the bars — the Grid's staff-axis comparator). */
  label: string;
  /** First name for the compact bar caption (first word of the label). */
  firstName: string;
  /** Coach-required bookings whose end time has passed at `now`. */
  done: number;
  /** Everything else — upcoming AND in-progress. */
  toCome: number;
  total: number;
}

/**
 * One row per coach on the **active staff roster** — including coaches with
 * ZERO bookings (a free coach must read as free, not absent — the same
 * principle as the Grid rendering every bay row). No roster (undefined/empty)
 * → no chart (`[]`): unlike the Grid axis there is no bookings-derived
 * degrade here, because a load chart built only from staff who happen to have
 * bookings would silently violate the zero-row rule. A booking whose
 * `assignedStaffId` is not on the roster is not charted (the roster is the
 * row population); rows sort with {@link compareStaffLabels} so this chart
 * and the Grid's By-Staff axis always agree on order.
 *
 * NOTE (recorded in the DNKADM-0298 delivery): the staff API has no
 * role/coach field, so the population is "active staff", not provably
 * "coaches" — correct at Sim Creek today where all active staff are coaches.
 * The metric is therefore labelled "coach-required bookings": a bar at zero
 * is truthful even for a non-coach.
 */
export function coachLoad(
  reservations: Reservation[],
  staffRoster: RosterEntry[] | undefined,
  now: Date,
): CoachLoadRow[] {
  if (!staffRoster || staffRoster.length === 0) return [];
  const rows = new Map<string, CoachLoadRow>(
    staffRoster.map((s) => [
      s.id,
      {
        id: s.id,
        label: s.label,
        firstName: s.label.trim().split(/\s+/)[0] ?? s.label,
        done: 0,
        toCome: 0,
        total: 0,
      },
    ]),
  );
  for (const r of reservations) {
    if (deriveStatusBucket(r.status) === 'cancelled') continue;
    if (r.assignedStaffId === null) continue;
    const row = rows.get(r.assignedStaffId);
    if (!row) continue;
    const end = r.endTime ? Date.parse(r.endTime) : NaN;
    if (!Number.isNaN(end) && end <= now.getTime()) row.done += 1;
    else row.toCome += 1;
    row.total += 1;
  }
  return [...rows.values()].sort(compareStaffLabels);
}

// ---- Room for a walk-in --------------------------------------------------------

export interface WalkInBay {
  id: string;
  label: string;
  /** No non-cancelled booking spans `now`. In-progress = not free, always. */
  free: boolean;
  /** No non-cancelled bookings on this bay at all today. */
  openAllDay: boolean;
  /**
   * For a FREE bay: the ISO start of the next booking after `now`, or null =
   * free until close. For a BUSY bay: null (freeing time is `busyUntil`).
   */
  freeUntil: string | null;
  /** For a BUSY bay: the ISO end of the booking spanning `now`, or null. */
  busyUntil: string | null;
}

export interface WalkInAvailability {
  /**
   * 'unknown' — operating hours are null (closed day / hours failed to load):
   * the panel must claim NOTHING it can't substantiate. 'closed' — `now` is
   * outside the operating window on the venue clock. 'ok' — derivations below
   * are meaningful.
   */
  status: 'ok' | 'unknown' | 'closed';
  bays: WalkInBay[];
  /** Headline count: bays free right now ('ok' only; otherwise 0). */
  freeNow: number;
}

/**
 * *Can I fit someone right now?* — derived per bay on the roster from the
 * loaded (already day-scoped) window. With no roster the lanes degrade to the
 * bays present in the bookings (the retired occupancy strip's fallback); with
 * neither, there are no lanes and the panel renders nothing.
 */
export function walkInAvailability(
  reservations: Reservation[],
  roster: RosterEntry[] | undefined,
  operating: HourRange | null,
  timeZone: string | null,
  now: Date,
): WalkInAvailability {
  if (operating === null) return { status: 'unknown', bays: [], freeNow: 0 };
  const hour = nowHourFraction(now, timeZone);
  if (hour < operating.startHour || hour >= operating.endHour) {
    return { status: 'closed', bays: [], freeNow: 0 };
  }

  const lanes: RosterEntry[] =
    roster ??
    [
      ...new Map(
        reservations
          .filter((r) => r.assignedResourceId !== null)
          .map((r) => [
            r.assignedResourceId ?? '',
            {
              id: r.assignedResourceId ?? '',
              label: r.assignedResourceName ?? r.assignedResourceId ?? '',
            },
          ]),
      ).values(),
    ].filter((l) => l.id !== '');

  const nowMs = now.getTime();
  const bays = lanes.map((lane): WalkInBay => {
    const live = reservations.filter(
      (r) => r.assignedResourceId === lane.id && deriveStatusBucket(r.status) !== 'cancelled',
    );
    const spanning = live.find((r) => {
      const start = r.startTime ? Date.parse(r.startTime) : NaN;
      const end = r.endTime ? Date.parse(r.endTime) : NaN;
      return !Number.isNaN(start) && !Number.isNaN(end) && start <= nowMs && nowMs < end;
    });
    if (spanning) {
      return {
        id: lane.id,
        label: lane.label,
        free: false,
        openAllDay: false,
        freeUntil: null,
        busyUntil: spanning.endTime,
      };
    }
    const nextStart = live
      .map((r) => (r.startTime ? Date.parse(r.startTime) : NaN))
      .filter((ms) => !Number.isNaN(ms) && ms > nowMs)
      .sort((a, b) => a - b)[0];
    return {
      id: lane.id,
      label: lane.label,
      free: true,
      openAllDay: live.length === 0,
      freeUntil: nextStart !== undefined ? new Date(nextStart).toISOString() : null,
      busyUntil: null,
    };
  });

  return { status: 'ok', bays, freeNow: bays.filter((b) => b.free).length };
}

// ---- Day counters ---------------------------------------------------------------

/**
 * The rail's Done / To come counters: ALL non-cancelled bookings on the loaded
 * day, split by whether their end has passed at `now` (same Done definition as
 * the coach chart, but not restricted to coach-required bookings).
 */
export function dayProgress(reservations: Reservation[], now: Date): { done: number; toCome: number } {
  let done = 0;
  let toCome = 0;
  for (const r of reservations) {
    if (deriveStatusBucket(r.status) === 'cancelled') continue;
    const end = r.endTime ? Date.parse(r.endTime) : NaN;
    if (!Number.isNaN(end) && end <= now.getTime()) done += 1;
    else toCome += 1;
  }
  return { done, toCome };
}
